import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, ClipboardCheck, ArrowRight } from "lucide-react";
import { usePageMeta } from "../../hooks/usePageMeta";
import FCFO3 from "../../assets/images/FCFO3.webp";
import FCFO4 from "../../assets/images/FCFO4.webp";
import projMan1 from "../../assets/images/projMan1.webp";

const CommercialFitOut = () => {
  usePageMeta(
    "Commercial Fit-Out | Sandhurst Interiors",
    "Commercial fit-out specialists in the South East. Full turnkey office and workspace fit-out—design, partitioning, ceilings, flooring, M&E and project management. 30+ years experience."
  );

  return (
    <div className="min-h-screen">
      <section className="section-padding bg-primary-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Commercial Fit-Out, Built Around Your Business
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Complete turnkey commercial fit-out—from design and space
              planning to partitioning, ceilings, flooring, M&E and project
              delivery across the South East.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700"
          >
            <p>
              Sandhurst Interiors delivers high-quality commercial fit-outs and
              refurbishment for offices, warehouses and industrial facilities
              across the South East. As a family-run business with over 30 years
              in the industry, we combine traditional craftsmanship with modern
              solutions so every space is functional, compliant and built to
              last.
            </p>
            <p>
              We provide complete design and planning, full project delivery and
              management, and bespoke workspace solutions—whether you need a
              full building fit-out or a focused upgrade. More than 70% of our
              projects come from returning clients, and we work with
              end-user clients and main contractors on projects of all sizes.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            What a Commercial Fit-Out Covers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "Design and space planning",
              "Office partitioning (soundproof, glazed, steel, timber)",
              "Dry-lining, taping & jointing and plastering",
              "Suspended ceilings and MF plasterboard ceilings",
              "Interior and exterior decorating",
              "Flooring (carpet tiles, vinyl, specialist finishes)",
              "Air conditioning and ventilation (design & installation)",
              "Electrical and data installation and maintenance",
              "Facilities management (planned and reactive)",
              "Full project and site management",
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg"
              >
                <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={FCFO3}
                  alt="Commercial fit-out project"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={FCFO4}
                  alt="Office and commercial fit-out"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="col-span-2 aspect-video rounded-lg overflow-hidden shadow-lg bg-white">
                <img
                  src={projMan1}
                  alt="Commercial fit-out project management"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center space-x-3 text-gray-700">
                <ClipboardCheck className="w-6 h-6 text-primary-600 flex-shrink-0" />
                <span className="font-medium">End-to-end project management</span>
              </div>
              <p className="text-gray-700">
                We manage contractors, budgets and timelines so you don't have
                to. With clear communication, tight scheduling and dedicated
                site supervision, we keep your commercial fit-out on track from
                start to finish—whether it's an office, warehouse or mixed-use
                facility.
              </p>
              <Link
                to="/contact#contact-form"
                onClick={() => sessionStorage.setItem("scrollToContactForm", "true")}
                className="btn-primary inline-flex items-center space-x-2"
              >
                <span>Get a Quote</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-padding bg-primary-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to start your commercial fit-out?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Speak with our team today about transforming your office,
              warehouse or facility.
            </p>
            <Link
              to="/contact#contact-form"
              onClick={() => sessionStorage.setItem("scrollToContactForm", "true")}
              className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-medium py-3 px-8 rounded-lg transition-all duration-200 inline-flex items-center space-x-2"
            >
              <Building2 className="w-4 h-4" />
              <span>Get in Touch</span>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CommercialFitOut;
